/**
 * WordPress dependencies
 */
import { useSelect, useDispatch } from '@safe-wordpress/data';
import { store as CORE } from '@safe-wordpress/core-data';
import { store as EDITOR } from '@safe-wordpress/editor';

/**
 * External dependencies
 */
import { every, filter, find } from 'lodash';

import type {
	Author,
	AttrSetter,
	Post,
	PostType,
	QueryResult,
	SearchQuery,
	SelectResult,
	Taxonomy,
	Term,
} from '@nelio/forms/types';

export function usePostMeta< A >(
	name: string,
	recordId?: number
): AttrSetter< A > {
	const meta = useSelect( ( select ) =>
		!! recordId
			? ( (
					select( CORE ).getEditedEntityRecord(
						'postType',
						'nelio_form',
						recordId
					) as Record< string, unknown >
			   )[ name ] as A )
			: ( select( EDITOR ).getEditedPostAttribute( name ) as A )
	);
	const { editEntityRecord } = useDispatch( CORE );
	const { editPost } = useDispatch( EDITOR );
	return [
		meta,
		( value: A ) => {
			const edits = { [ name ]: value };
			const options = { undoIgnore: true };
			return !! recordId
				? editEntityRecord(
						'postType',
						'nelio_form',
						recordId,
						edits,
						options
				  )
				: editPost( edits, options );
		},
	] as const;
} //end usePostMeta()

export function useAuthors(): ReadonlyArray< Author > {
	return useSelect( ( select ) => select( CORE ).getAuthors() ) || [];
} //end usePostTypes()

export function usePostTypes(): ReadonlyArray< PostType > {
	const postTypes = useAllPostTypes();
	return filter( postTypes, ( p ) => p.viewable && 'nelio_form' !== p.slug );
} //end usePostTypes()

export function useAllowedPostTypes(): ReadonlyArray< PostType > {
	const postTypes = useAllAllowedPostTypes();
	return filter( postTypes, ( p ) => p.viewable && 'nelio_form' !== p.slug );
} //end usePostTypes()

export function useTaxonomies( postType?: string ): ReadonlyArray< Taxonomy > {
	const taxonomies = useAllTaxonomies();
	return filter(
		taxonomies,
		( t ) => ! postType || t.types.includes( postType )
	);
} //end useTaxonomies()

export function useEntityKind(
	kind: 'postType' | 'taxonomy',
	name: string
): PostType | Taxonomy | undefined {
	const postTypes = useAllPostTypes();
	const taxonomies = useAllTaxonomies();
	const types: ReadonlyArray< PostType | Taxonomy > =
		'postType' === kind ? postTypes : taxonomies;

	return find( types, { slug: name } );
} //end useEntityKind()

export function useEntityRecordSearch(
	kind: 'postType' | 'taxonomy',
	name: string,
	query: SearchQuery
): QueryResult< Post | Term > {
	query = { per_page: 10, ...query };

	const records = useSelect( ( select ) =>
		select( CORE ).getEntityRecords( kind, name, query )
	);

	const hasFinished = useSelect( ( select ) =>
		select( CORE ).hasFinishedResolution( 'getEntityRecords', [
			kind,
			name,
			query,
		] )
	);

	if ( ! hasFinished ) {
		return { finished: false };
	} //end if

	const items: ReadonlyArray< Post | Term > =
		'postType' === kind
			? filter( records, isPost )
			: filter( records, isTerm );
	return {
		finished: true,
		items,
		more: query.per_page === items.length,
	};
} //end useEntityRecordSearch()

export function useEntityRecords(
	kind: 'postType' | 'taxonomy',
	name: string,
	itemIds: ReadonlyArray< number >
): SelectResult< Post | Term > {
	const records = useSelect( ( select ) => {
		const { getEntityRecord } = select( CORE );
		return itemIds.map( ( itemId ) =>
			getEntityRecord( kind, name, itemId )
		);
	} );

	const finishedStatuses = useSelect( ( select ) => {
		const { hasFinishedResolution } = select( CORE );
		return itemIds.map( ( itemId ) =>
			hasFinishedResolution( 'getEntityRecord', [ kind, name, itemId ] )
		);
	} );

	const hasFinished = every( finishedStatuses );
	const items: ReadonlyArray< Post | Term > =
		'postType' === kind
			? filter( records, isPost )
			: filter( records, isTerm );
	const loadedItemIds = items.map( ( { id } ) => id );
	const pendingItems = itemIds.filter(
		( id ) => ! loadedItemIds.includes( id )
	);
	return ! hasFinished
		? { finished: false, items, pendingItems }
		: { finished: true, items, missingItems: pendingItems };
} // end useEntityRecords()

export const useCanUser = (
	action: 'create' | 'read' | 'update' | 'delete',
	resource: string,
	id: number | string
): { readonly hasResolvedUserCan: boolean; readonly userCan?: boolean } =>
	useSelect( ( select ) => {
		const userCan: boolean | undefined = select( CORE ).canUser(
			action,
			resource,
			id
		);
		const hasResolvedUserCan = !! select( CORE ).hasFinishedResolution(
			'canUser',
			[ action, resource, id ]
		);
		return {
			hasResolvedUserCan,
			userCan,
		};
	} );

// =======
// HELPERS
// =======

const useAllPostTypes = (): ReadonlyArray< PostType > =>
	useSelect( ( select ) =>
		select( CORE ).getPostTypes( { per_page: -1 } )
	) || [];

const useAllAllowedPostTypes = (): ReadonlyArray< PostType > | undefined =>
	useSelect(
		( select ) =>
			select( CORE )
				.getPostTypes( { per_page: -1 } )
				?.filter( ( p ) =>
					select( CORE ).canUser( 'create', p.rest_base, '' )
				)
	);

const useAllTaxonomies = (): ReadonlyArray< Taxonomy > =>
	filter(
		useSelect( ( select ) =>
			select( CORE ).getTaxonomies( { per_page: -1 } )
		),
		( t ) => t.visibility.public
	);

const isPost = ( p?: unknown ): p is Post =>
	!! p &&
	'object' === typeof p &&
	'id' in p &&
	!! p.id &&
	'slug' in p &&
	!! p.slug &&
	'type' in p &&
	!! p.type &&
	'title' in p &&
	!! p.title &&
	'object' === typeof p.title &&
	'raw' in p.title &&
	!! p.title.raw;

const isTerm = ( t: unknown ): t is Term =>
	!! t &&
	'object' === typeof t &&
	'id' in t &&
	!! t.id &&
	'slug' in t &&
	!! t.slug &&
	'name' in t &&
	!! t.name;
